import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { userApi, getUserToken, getUserName, clearUserToken } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import PostListRow from "@/components/PostListRow";
import PostDialog from "@/components/PostDialog";
import { Search, LogOut } from "lucide-react";
import { toast } from "sonner";

const Reserved = () => {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 25;
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const username = getUserName();

  useEffect(() => {
    if (!getUserToken()) {
      nav("/accedi", { replace: true });
      return;
    }
    let cancel = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await userApi.get("/reserved/posts", {
          params: { q, page, per_page: perPage },
        });
        if (cancel) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (!cancel && err?.response?.status !== 401) {
          toast.error("Errore caricamento");
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    }, 200);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, page, nav]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const logout = () => {
    clearUserToken();
    toast.success("Disconnesso");
    nav("/", { replace: true });
  };

  return (
    <div data-testid="reserved-page" className="min-h-screen bg-[#f9f8f6]">
      <PublicHeader />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-24">
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#9e4747]">Riservati</p>
          {username && (
            <button
              data-testid="user-logout"
              onClick={logout}
              className="flex items-center gap-1.5 font-sans text-[10px] tracking-[0.25em] uppercase text-[#4a4843] hover:text-[#1a1918]"
            >
              <LogOut size={11} /> esci
            </button>
          )}
        </div>
        <h1 className="font-serif text-4xl sm:text-6xl tracking-tight leading-none font-light text-[#1a1918] mb-4">
          Spazio di {username || "lettore"}
        </h1>
        <p className="font-serif italic text-[#4a4843] mb-10 sm:mb-12 leading-relaxed max-w-prose">
          Pagine condivise solo con chi ha un accesso. Custodisci queste parole.
        </p>

        <div className="relative mb-10 sm:mb-12 max-w-xl">
          <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#4a4843]" />
          <input
            data-testid="reserved-search"
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="cerca un titolo…"
            className="w-full bg-transparent border-0 border-b border-[#1a1918] py-3 pl-7 pr-2 font-serif text-lg focus:outline-none focus:border-[#d4b886]"
          />
        </div>

        {loading ? (
          <p data-testid="reserved-loading" className="font-sans text-sm text-[#4a4843]">caricamento…</p>
        ) : items.length === 0 ? (
          <p data-testid="reserved-empty" className="font-serif italic text-xl text-[#4a4843] py-12 text-center">
            Nessun post riservato per ora.
          </p>
        ) : (
          <ul className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]" data-testid="reserved-list">
            {items.map((p) => (
              <li key={p.id} data-testid={`reserved-item-${p.id}`}>
                <PostListRow post={p} onOpen={setOpenId} variant="compact" />
              </li>
            ))}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-between gap-3">
            <button
              data-testid="reserved-prev"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="font-sans text-xs tracking-[0.3em] uppercase border-b border-[#1a1918] pb-1 disabled:opacity-30 py-2"
            >
              ← prec
            </button>
            <span className="font-sans text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#4a4843]">
              {page} / {totalPages}<span className="hidden sm:inline"> — {total} post</span>
            </span>
            <button
              data-testid="reserved-next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="font-sans text-xs tracking-[0.3em] uppercase border-b border-[#1a1918] pb-1 disabled:opacity-30 py-2"
            >
              succ →
            </button>
          </div>
        )}

        <p className="text-center mt-12">
          <Link to="/" className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#4a4843] hover:text-[#1a1918]">
            ← diario pubblico
          </Link>
        </p>
      </section>

      <PostDialog
        postId={openId}
        open={!!openId}
        onOpenChange={(v) => !v && setOpenId(null)}
        apiClient={userApi}
        basePath="/reserved/posts"
      />
      <PublicFooter />
    </div>
  );
};

export default Reserved;
